import { useParams } from "react-router";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { Card, KpiHeadline, MetaLabel, RowList } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { useDetailTopBar } from "@acto/taskpane/field-sales-2/components/patterns/DetailTopBar/DetailTopBar";
import { LineItemRow } from "@acto/taskpane/field-sales-2/components/primitives/LineItemRow/LineItemRow";
import { formatNumericDate } from "@acto/taskpane/field-sales-2/utils/format";
import { useOfferDetails } from "@acto/taskpane/field-sales-2/data-access/useOfferDetails";

const SectionHeading = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

export function OfferDetails() {
  const t = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();
  const { offerId } = useParams<{ offerId: string }>();

  const { isPending, error, offer } = useOfferDetails(offerId);
  const { sentinelRef, topBar } = useDetailTopBar({
    title: offer?.externalReference,
    subtitle: offer?.formattedValue,
    backFallbackUrl: "/home",
  });

  return (
    <>
      {topBar}
      <PageLayout hPadding="card" isLoading={isPending} isError={!!error} isEmpty={!offer}>
        {offer && (
          <>
            <Card>
              <SectionHeading ref={sentinelRef}>{offer.externalReference}</SectionHeading>
              <Divider />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <MetaLabel>
                  {t("offerDetails.valueLabel")}
                  {offer.validFrom != null && ` · ${formatNumericDate(offer.validFrom, locale)}`}
                </MetaLabel>
                <KpiHeadline>{offer.formattedValue}</KpiHeadline>
              </div>
            </Card>

            <SectionHeading style={{ paddingLeft: 20 }}>{t("offerDetails.lineItems.title")}</SectionHeading>

            <Card>
              <RowList role="list">
                {offer.lineItems.map((item) => (
                  <LineItemRow
                    key={item.id}
                    name={item.productName}
                    price={item.formattedValue}
                    meta={item.productExternalReference}
                  />
                ))}
              </RowList>
            </Card>
          </>
        )}
      </PageLayout>
    </>
  );
}
